import Swal, { SweetAlertResult as SAResult, SweetAlertOptions } from 'sweetalert2'
export interface SweetAlertResult extends SAResult {}

export const bmSwal = Swal.mixin({
  customClass: {
    confirmButton: 'btn btn-secondary mx-1',
    cancelButton: 'btn btn-outline-secondary mx-1'
  },
  buttonsStyling: false
})

export const sweetAlert = {
  confirm: (opts?: SweetAlertOptions, separator: boolean = true): Promise<boolean> => {
    const content = opts.html || opts.text
    if (separator && content) {
      opts.html = `<hr class="w-25 ml-0 brand-hr"><br>${content}`
    }
    return new Promise((res, rej) => {
      bmSwal.fire({
        title: "Are you sure?",
        text: '',
        showCancelButton: true,
        allowOutsideClick: false,
        allowEscapeKey: false,
        customClass: {
          title: "w-100",
          htmlContainer: "text-left",
          confirmButton: 'btn btn-secondary',
          cancelButton: 'btn btn-outline-secondary ml-2',
          actions: "justify-content-start"
        },
        ...(opts || {}),
        willOpen: () => {
          const msg = { iframe: { action: 'open-modal' } }
          window.parent.postMessage(msg, '*')
        },
        willClose: () => {
          const msg = { iframe: { action: 'close-modal' } }
          window.parent.postMessage(msg, '*')
        }
      }).then(result => {
        res(result.value || false)
      })
    })
  },

  success: (opts?: SweetAlertOptions): Promise<SweetAlertResult> => {
    return bmSwal.fire({
      icon: 'success',
      title: 'Success',
      text: '',
      ...(opts || {})
    })
  },

  dirtyFormAlert: (opts?: SweetAlertOptions): Promise<boolean> => {
    return new Promise((res) => {
      bmSwal.fire({
        title: "Are you sure?",
        text: "You have unsaved changes that will be lost",
        showCancelButton: true,
        allowOutsideClick: false,
        allowEscapeKey: false,
        ...(opts || {})
      }).then(result => {
        res(result.value || false)
      })
    })
  },

  // post-request-edits.page.ts
  // TODO: Check that this works
  getComment: (): Promise<string> => {
    return new Promise((res, rej) => {
      bmSwal.fire({
        title: "Enter a Comment",
        input: 'text',
        showCancelButton: true,
      }).then(({value}) => {
        res(value)
      })
    })
  },

  // keywords.page.ts
  saveKeyword: (keywordState = null, canSaveProgress = true, {
    onAbandonChanges,
    onContinueEditing,
    onSaveAsActive,
    onSaveAsDraft
  }: {
    onAbandonChanges?: Function,
    onContinueEditing?: Function,
    onSaveAsActive?: Function,
    onSaveAsDraft?: Function
  }): Promise<SweetAlertResult> => {
    let saveButton
    if (canSaveProgress) {
      switch (keywordState) {
        case 'active':
          saveButton = {
            text: 'Update Request',
            value: 'saveAsActive'
          }
          break

        case 'fulfilled':
        case 'deleted':
          saveButton = {
            text: 'Save as Draft',
            value: 'saveAsDraft'
          }
          break

        // 'paused' or creating new keyword
        default:
          saveButton = {
            text: 'Save Progress',
            value: 'saveAsDraft'
          }
      }
      return bmSwal.fire({
        title: 'Save Progress?',
        html: `
          <div>If you leave the page without saving you will lose your progress.</div>
          <div class="mt-3">
            <button class="btn btn-outline-secondary btn-block-sm-down" id="abandonChanges">Abandon Changes</button>
            <button class="btn btn-outline-secondary btn-block-sm-down ml-md-1 mt-2 mt-md-0" id="continueEditing">Continue Editing</button>
            <button class="btn btn-secondary btn-block-sm-down ml-md-1 mt-2 mt-md-0" id="${saveButton.value}">${saveButton.text}</button>
          </div>
        `,
        showConfirmButton: false,
        willOpen: el => {
          const abandonChanges = el.querySelector('#abandonChanges')
          abandonChanges.addEventListener('click', () => {
            Swal.close()
            onAbandonChanges()
          })

          const continueEditing = el.querySelector('#continueEditing')
          continueEditing.addEventListener('click', () => {
            Swal.close()
            onContinueEditing()
          })

          if (saveButton.value === 'saveAsDraft') {
            const saveAsDraft = el.querySelector(`#saveAsDraft`)
            saveAsDraft.addEventListener('click', () => {
              Swal.close()
              onSaveAsDraft()
            })
          }
          if (saveButton.value === 'saveAsActive') {
            const saveAsActive = el.querySelector('#saveAsActive')
            saveAsActive.addEventListener('click', () => {
              Swal.close()
              onSaveAsActive()
            })
          }
        }
      })
    } else {
      return Promise.resolve(null)
    }
  },
  newVideoSubscriptionSuccess: (): Promise<SweetAlertResult> => {
    return bmSwal.fire({
      title: 'Success!',
      html: `
        <div class="h3-subheader">
          <div>You've added a new subscription.</div>
          <div>
            <strong>Create a request</strong>
            to get our team working on your project right away.
          </div>
        </div>
      `,
      confirmButtonText: `Create a request`,
      icon: 'success'
    })
  },
  openPaymentFailedModal(verbose: string = ''): Promise<SweetAlertResult> {
    return bmSwal.fire({
      title: 'Transaction Declined',
      html: `
        <div class="m-3">Oh dear, the transaction was declined. Please update the payment method (or contact your bank).</div>
        ${verbose}
      `,
      confirmButtonText: 'Update payment',
      showCancelButton: true,
      cancelButtonText: 'Go back',
      reverseButtons: true,
    })
  },
  migrationCandidateAlert: (): Promise<SweetAlertResult> => {
    return bmSwal.fire({
      html: `
        <div class="mb-4">
          ⭐⭐⭐⭐⭐
        </div>
        <div class="pb-4">
          <h3>A NEW FLEXIBLE SYSTEM IS HERE!</h3>
        </div>
        <p class="text-left p-1">
          One of the most requested features from our customers is the ability to request content of different word lengths within the same subscription. We have made that happen and more!
        </p>
        <ul class="text-left">
          <li>
            <b>New Flexibility:</b> you can now request content of varying word lengths within the same subscription.
          </li>
          <li>
            <b>New Content Enhancements:</b> you can now choose optional upgrade services like +Photo whenever you request a piece of content, even if it’s not on your plan.
          </li>
          <li>
            <b>New Ways to Save:</b> We’re offering new plan lengths and services—check them out on the ‘Change Plan’ page.
          </li>
          <li>
          <b>Pricing:</b> These new features do not come with a significant price increase for you, but you will see a change of a few cents on your next invoice, due to the new system using whole dollars only, not cents.
          </li>
        </ul>
      `,
      customClass: {
        popup: 'rounded-0 p-5',
        confirmButton: 'btn btn-primary',
      },
      confirmButtonText: 'More Details',
      showCloseButton: true,
      allowOutsideClick: false,
      allowEscapeKey: false,
    })
  }
}

export default sweetAlert
